'use client';

import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouter } from 'src/routes/hooks';
import { toast } from 'src/components/snackbar';
import { allLangs } from './all-langs';
import { fallbackLng, changeLangMessages as messages } from './config-locales';
import { useSettingsContext } from '@components/settings';

// ----------------------------------------------------------------------

export function useTranslate(ns) {
  const router = useRouter();
  const { t, i18n } = useTranslation(ns);
  const settings = useSettingsContext();

  const fallback = allLangs.find((lang) => lang.value === fallbackLng);
  const currentLang = allLangs.find((lang) => lang.value === i18n.resolvedLanguage) ?? fallback;

  const [isClient, setIsClient] = useState(false);


  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const storedLang = localStorage.getItem('selectedLang');
    if (storedLang && storedLang !== i18n.resolvedLanguage) {
      i18n.changeLanguage(storedLang);
      const storedDirection = localStorage.getItem('direction') || (storedLang === 'ar' ? 'rtl' : 'ltr');
      settings.onUpdateField('direction', storedDirection);
      dayjs.locale(storedLang === 'ar' ? 'ar' : 'en');
    }
  }, [isClient]); 

  const onChangeLang = useCallback(
    async (newLang) => {
      try {
        if (newLang === i18n.resolvedLanguage) return;

        const langChangePromise = i18n.changeLanguage(newLang);
        const currentMessages = messages[newLang] || messages.en;

        toast.promise(langChangePromise, {
          loading: currentMessages.loading,
          success: () => currentMessages.success,
          error: currentMessages.error,
        });

        dayjs.locale(newLang === 'ar' ? 'ar' : 'en');

        if (typeof window !== 'undefined') {
          localStorage.setItem('selectedLang', newLang);
          localStorage.setItem('direction', newLang === 'ar' ? 'rtl' : 'ltr');
          settings.onUpdateField('direction', newLang === 'ar' ? 'rtl' : 'ltr');
        }

        router.refresh();
      } catch (error) {
        console.error(error);
      }
    },
    [i18n, router, settings]
  );

  return { t, i18n, onChangeLang, currentLang };
}
